import React, { useEffect, useState } from "react";
import { forEach, filter, isEmpty, map } from "lodash";
import { Button, Dialog, Icon, Tab } from "@alifd/next";
import { batchMaterialListUsingGet } from "../../service/accept/batchForm";
import styles from "./index.module.scss";

const countMaterial = (materials) => {
  if (isEmpty(materials)) {
    return "";
  }
  return materials.length;
};

const convertMaterialType = (materialForms) => {
  if (!materialForms) {
    return "none";
  }
  if (materialForms.hasOwnProperty("1")) {
    return "paper";
  } else if (materialForms.hasOwnProperty("2")) {
    return "electronic";
  } else if (materialForms.hasOwnProperty("3")) {
    return "all";
  } else {
    return "none";
  }
};

// 将材料转换为标准的数据格式
const convertMaterialData = (props) => {
  const {
    materialCode,
    materialName,
    desc,
    materialForms,
    includeFileExtensions,
  } = props || {};

  let extensions;
  if (!isEmpty(includeFileExtensions)) {
    extensions = `支持${includeFileExtensions.join(",")}格式`;
  }

  return {
    code: materialCode,
    label: materialName,
    desc,
    extensions,
    type: convertMaterialType(materialForms),
  };
};

// 材料标签
function MaterialLabel(props) {
  const { type } = props;
  return (
    <div className={styles.material_label__group}>
      {(type === "all" || type === "electronic") && (
        <div className={styles.material_label__button}>电子</div>
      )}
      {(type === "all" || type === "paper") && (
        <div className={styles.material_label__button}>纸质</div>
      )}
    </div>
  );
}

// 材料分组
function MaterialGroup(props) {
  const { label, desc, materials } = props;
  return (
    <li className={styles.listItem}>
      <div className={styles.listItemIcon} />
      <div className={styles.listItemContent}>
        <div className={styles.listItemTitle}>
          {label}
          <span className={styles.listItemGroupDesc}>{`(${desc})`}</span>
        </div>
        {materials.map((material) => {
          const {
            label: itemLabel,
            type,
            desc: itemDesc,
            extensions,
          } = material;
          return (
            <div className={styles.listItemGroupContent}>
              <div className={styles.listItemGroupTitle}>
                {itemLabel}
                <MaterialLabel type={type} />
              </div>
              <div className={styles.listItemDesc}>{itemDesc}</div>
              <div className={styles.listItemDesc}>{extensions}</div>
            </div>
          );
        })}
      </div>
    </li>
  );
}

// 材料项
function MaterialItem(props) {
  const { label, type, desc, extensions } = props;

  return (
    <li className={styles.listItem}>
      <div className={styles.listItemIcon} />
      <div className={styles.listItemContent}>
        <div className={styles.listItemTitle}>
          {label}
          <MaterialLabel type={type} />
        </div>
        <div className={styles.listItemDesc}>{desc}</div>
        <div className={styles.listItemDesc}>{extensions}</div>
      </div>
    </li>
  );
}

export default function MaterialList(props) {
  const { sequenceNo, showMaterialList } = props;
  const [visible, setVisible] = useState(showMaterialList);
  const [data, setData] = useState();
  const [materialGroups, setMaterialGroups] = useState([]);
  const [materialsNoGroup, setMaterialsNoGroup] = useState([]);

  const fetchData = async () => {
    // 查询并展示需要的材料列表
    const res = await batchMaterialListUsingGet({ sequenceNo });
    setData(res.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 每当数据变化，重新计算材料分组
  useEffect(() => {
    const materialMap = {};
    const groupMaterialCodes = [];
    const materialGroupsData = [];

    forEach(
      data?.materials,
      (material) => (materialMap[material?.materialCode] = material)
    );

    forEach(data?.materialGroups, (materialGroup) => {
      groupMaterialCodes.push(...materialGroup?.materialCodes);
      const existedMaterialCodes = filter(
        materialGroup?.materialCodes,
        (code) => !!materialMap[code]
      );
      materialGroupsData.push({
        label: materialGroup?.groupTitle,
        desc: materialGroup?.groupTip,
        materials: map(existedMaterialCodes, (code) => {
          return convertMaterialData(materialMap[code]);
        }),
      });
    });

    setMaterialGroups(materialGroupsData);
    // 没有分组的材料列表
    setMaterialsNoGroup(
      filter(
        data?.materials,
        (item) => !groupMaterialCodes.includes(item?.materialCode)
      )
    );
  }, [data]);

  const onClick = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  return (
    <div className={styles.container}>
      <Button onClick={onClick}>
        <Icon type="list" />
        材料清单
      </Button>
      <Dialog
        className={styles.dialog}
        title="材料清单"
        shouldUpdatePosition
        visible={visible}
        onCancel={onClose}
        onClose={onClose}
        footer={
          <Button type="normal" onClick={onClose}>
            关闭
          </Button>
        }
      >
        <div className={styles.content}>
          <Tab size="small" shape="text">
            <Tab.Item
              key="all"
              title={`合并后全部材料 ${countMaterial(data?.materials)}`}
            >
              {map(materialGroups, (materialGroup, index) => {
                return <MaterialGroup key={index} {...materialGroup} />;
              })}
              {map(materialsNoGroup, (material, index) => {
                const materialData = convertMaterialData(material);
                return <MaterialItem key={index} {...materialData} />;
              })}
            </Tab.Item>
            <Tab.Item
              key="electronic"
              title={`需提交的电子材料 ${countMaterial(data?.electronicMaterials)}`}
            >
              {map(data?.electronicMaterials, (material, index) => {
                const materialData = convertMaterialData(material);
                return <MaterialItem key={index} {...materialData} />;
              })}
            </Tab.Item>
            <Tab.Item
              key="paper"
              title={`需提交的纸质材料 ${countMaterial(data?.paperMaterials)}`}
            >
              {map(data?.paperMaterials, (material, index) => {
                const materialData = convertMaterialData(material);
                return <MaterialItem key={index} {...materialData} />;
              })}
            </Tab.Item>
          </Tab>
        </div>
      </Dialog>
    </div>
  );
}
